import { useCallback, useState } from 'react';
import Axios, { AxiosResponse } from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { environment } from '../../environment';
import { errorCatcher, snackbarSettingsAtom } from '../../hooks';
import { useLogin } from '../login';
import {
    InspoCandidate,
    InspoFeatureInput,
    InspoFeatureRow,
    InspoPayload,
    inspoFailedAtom,
    inspoLoadingAtom,
    inspoPayloadAtom
} from './types';

// The Home teaser and the Inspo page mount together: one request between them.
let inFlight: Promise<InspoPayload | null> | null = null;

// #region useInspo
/**
 * The Inspo payload, shared through an atom. `load` is a no-op once loaded unless forced.
 * `publicMode` reads the no-login endpoint (public stories page and the wallmates.com embed).
 */
export const useInspo = (publicMode = false) => {
    const [payload, setPayload] = useAtom(inspoPayloadAtom);
    const [loading, setLoading] = useAtom(inspoLoadingAtom);
    const [failed, setFailed] = useAtom(inspoFailedAtom);
    const [, authToken, loginFunctions] = useLogin();

    const load = useCallback(async (force = false) => {
        if (payload && !force) return;
        if (!inFlight) {
            setLoading(true);
            const request = publicMode
                ? Axios.get<InspoPayload>(`${environment.v2Url}/public/inspo`)
                : Axios.get<InspoPayload>(`${environment.v2Url}/inspo`, { headers: { 'X-Wallmates-Auth': authToken } });
            inFlight = request
                .then((res) => res.data)
                .catch((err) => {
                    // Nobody is signed in on the public page, so never log anyone out from there.
                    if (!publicMode) errorCatcher(err, loginFunctions.logout);
                    return null;
                })
                .finally(() => { inFlight = null; });
        }
        const data = await inFlight;
        setPayload(data);
        setFailed(data === null);
        setLoading(false);
    }, [payload, publicMode, authToken, loginFunctions.logout, setPayload, setLoading, setFailed]);

    return { payload, loading, failed, load };
};

// #region useInspoAdmin
/** Superadmin: the ranked brands with exact numbers, and saving a brand's consent and story. */
export const useInspoAdmin = () => {
    const [candidates, setCandidates] = useState<InspoCandidate[]>([]);
    const [loading, setLoading] = useState(false);
    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const list = async () => {
        setLoading(true);
        const res: AxiosResponse<InspoCandidate[]> | void = await Axios.get(`${environment.v2Url}/inspo/admin/candidates`, { headers })
            .catch((err) => errorCatcher(err, loginFunctions.logout));
        if (res) setCandidates(res.data ?? []);
        else setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to load Inspo brands' });
        setLoading(false);
    };

    const save = async (brandId: number, input: InspoFeatureInput): Promise<InspoFeatureRow | null> => {
        const res: AxiosResponse<InspoFeatureRow> | void = await Axios.put(`${environment.v2Url}/inspo/admin/${brandId}`, input, { headers })
            .catch((err) => {
                errorCatcher(err, loginFunctions.logout);
                const data = err?.response?.data;
                const message = typeof data?.trace === 'string' ? data.trace : data?.message;
                // Backend messages start with "[Class][method]: "; the reason after it is what helps.
                const text = typeof message === 'string' ? message.replace(/^(\[[^\]]*\])+:\s*/, '') : 'Failed to save';
                setSnackbar({ show: true, snackbarLevel: 'error', text });
            });
        if (!res) return null;
        setCandidates((prev) => prev.map((c) => (c.brand_id === brandId ? { ...c, feature: res.data } : c)));
        setSnackbar({ show: true, snackbarLevel: 'info', text: 'Saved' });
        return res.data;
    };

    return { candidates, loading, list, save };
};
